import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Calendar, User, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

const PersonCardMobile = ({ person, type = 'missing', index = 0 }) => {
  const isMissing = type === 'missing';

  const getPhotoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API}/api/files/${path}`;
  };

  const genderMap = {
    'male': 'पुरुष',
    'female': 'महिला',
    'other': 'अन्य',
    'पुरुष': 'पुरुष',
    'महिला': 'महिला',
    'अन्य': 'अन्य'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link 
        to={`/case/${person.case_id}`}
        className="block"
        data-testid={`person-card-${person.case_id}`}
      >
        <div className="app-card transition-transform duration-200">
          {/* Image with overlay */}
          <div className="relative aspect-[4/3] bg-gray-100">
            {person.photo_url ? (
              <img
                src={getPhotoUrl(person.photo_url)}
                alt={person.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                <User className="w-16 h-16 text-gray-300" />
              </div>
            )}
            
            {/* Status Badge */}
            <div className="absolute top-3 left-3">
              <span className={`status-badge-glass px-3 py-1.5 text-xs font-bold rounded-full`}>
                {isMissing ? 'लापता' : 'अज्ञात'}
              </span>
            </div>

            {/* Quick Info Overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pt-12">
              <h3 className="font-outfit font-bold text-lg text-white truncate">
                {person.name}
              </h3>
              <div className="flex items-center gap-3 text-white/80 text-sm mt-1">
                <span>{person.age} वर्ष</span>
                <span>•</span>
                <span>{genderMap[person.gender] || person.gender}</span>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="p-4 space-y-3">
            {/* Location */}
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-gray-600 truncate">
                  {isMissing ? person.last_seen_location : person.found_location}
                </p>
                <p className="text-xs text-gray-400">{person.city}, {person.state}</p>
              </div>
            </div>

            {/* Date */}
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <p className="text-sm text-gray-600">
                {isMissing ? person.last_seen_date : person.found_date}
              </p>
            </div>

            {/* Contact */}
            {person.reporter_mobile && (
              <span 
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `tel:${person.reporter_mobile}`; }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                  isMissing 
                    ? 'bg-red-50 text-red-600 hover:bg-red-100' 
                    : 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                }`}
                data-testid={`contact-${person.case_id}`}
              >
                <Phone className="w-4 h-4" />
                {person.reporter_mobile}
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default PersonCardMobile;
